import { MenuItem } from 'types/GenericTypes';
import { PaymentMethodsValue } from 'mycheck-core';

export interface ICheckoutApi {
  postCreateCart: (
    model: HttpCheckoutApiRequest['updateCart'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iCreateNewCart']>;
  putUpdateCart: (
    model: HttpCheckoutApiRequest['updateCart'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iCreateNewCart']>;
  postCheckoutCart: (
    model: HttpCheckoutApiRequest['checkoutCart'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iCheckoutCart']>;
  deleteCheckoutCart: (cartId: string, accessToken: string) => Promise<void>;
  roomValidation: (
    model: HttpCheckoutApiRequest['roomValidation'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iRoomValidation']>;
  roomValidity: (
    model: HttpCheckoutApiRequest['roomValidation'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iRoomValidation']>;
  confirm3DSecure: (
    orderId: string,
    paymentId: number,
    transactionId: string | number,
  ) => Promise<void>;
  cancelTransaction: (orderId: string, paymentId: number) => Promise<void>;
  getOrderGuestStatus: (
    id: string,
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iOrderGuestStatus']>;
  setGuestOrderStatusNotificationOpt: (
    model: HttpCheckoutApiRequest['setGuestOrderStatusNotificationOpt'],
    accessToken: string,
  ) => Promise<void>;
  createAstralWalletTransaction: (
    model: HttpCheckoutApiRequest['createAstralWalletTransaction'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iCreateAstralWalletTransaction']>;
  getAstralWalletIframeData: (
    model: HttpCheckoutApiRequest['getAstralWalletIframeData'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iGetAstralWalletIframeData']>;
  astralWalletTransactionStatus: (
    model: HttpCheckoutApiRequest['astralWalletTransactionStatus'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iAstralWalletTransactionStatus']>;
  refundAstralWalletPayment: (
    model: HttpCheckoutApiRequest['refundAstralWalletPayment'],
    accessToken: string,
  ) => Promise<void>;
  getOrderConfirmationDetails: (
    model: HttpCheckoutApiRequest['getOrderConfirmationDetails'],
    accessToken: string,
  ) => Promise<HttpCheckoutApiResponse['iGetOrderDetailsConfirmation']>;
  retryAstralWalletCheckout: (
    model: HttpCheckoutApiRequest['retryAstralWalletCheckout'],
    accessToken: string,
  ) => Promise<void>;
}

type ServiceCharge = {
  name: string;
  amount: number;
};

interface UpdateCart {
  id: string;
  business_id?: string;
  experience_id?: string;
  created_at?: string;
  details?: CartDetailsType;
  items?: CartItemRequest[];
  payment?: CartPaymentType;
  receipt?: CartReceiptType;
  comment?: string;
  checkout_at?: string;
}

type TipType = {
  type: 'FIXED' | 'PERCENTAGE';
  amount: string;
};

interface checkoutCartType extends UpdateCart {
  checkout_at: string;
  number_of_diners?: string;
  tip?: TipType;
}

interface AstralWalletBasePaymentProps {
  paymentId: string;
}

export interface CreateAstralWalletTransactionType {
  cart_id: string;
  dev_mode: boolean;
  tip?: TipType;
}

export interface CreateAstralWalletTransactionResponseType
  extends AstralWalletBasePaymentProps {}

export interface GetAstralWalletIframeDataType
  extends AstralWalletBasePaymentProps {}

export interface GetAstralWalletIframeDataResponseType {
  url: string;
}
export enum OrderStatusName {
  CANCELLED = 'Cancelled',
  RECEIVED = 'Received',
  PREPARING = 'Preparing',
  PREPARING_NOT_PAID = 'Preparing_Not_Paid',
  READY = 'Ready',
  READY_NOT_PAID = 'Ready_Not_Paid',
}

export type OrderGuestStatuses = {
  name: OrderStatusName;
  dateTime: {
    date: string;
    timezone_type: number;
    timezone: string;
  };
};

export interface OrderGuestStatusType {
  businessId: number;
  experienceId: number;
  orderId: number;
  dueTime: string;
  maskedEmail: string | null;
  maskedPhone: string | null;
  statuses: OrderGuestStatuses[];
}

export interface AstralWalletTransactionStatusType {
  uuid: string;
}

export interface RefundAstralWalletPaymentType {
  uuid: string;
}

export interface RetryAstralWalletCheckoutType {
  paymentId: string;
}

export interface GetOrderDetailsConfirmationType {
  orderId: string;
}

export interface GetOrderDetailsConfirmationResponseType {
  experience_id: string;
  confirmation_number: string;
  checkout_at: string;
  guest_order_status_id: string;
}

export enum AstralWalletPaymentType {
  SALE = 'sale',
  AUTH = 'auth',
}

export enum AstralWalletTransactionStatus {
  PENDING = 'pending',
  CREATED = 'created',
  IN_PROGRESS = 'in-progress',
  AUTHORISED = 'authorised',
  SUCCESS = 'success',
  FAILED = 'failed',
  ERROR = 'error',
  CANCELED = 'canceled',
}

export type AstralWalletPaymentStatus = Exclude<
  AstralWalletTransactionStatus,
  'canceled'
>;
export type AstralWalletOrderStatus = Exclude<
  AstralWalletTransactionStatus,
  'authorised' | 'created'
>;

type AstralWalletPaymentStatusDataType = {
  id: string;
  type: AstralWalletPaymentType;
  status: AstralWalletPaymentStatus;
};

type AstralWalletOrderStatusDataType = {
  id: string;
  status: AstralWalletOrderStatus;
};

export interface AstralWalletTransactionStatusResponseType {
  payment: AstralWalletPaymentStatusDataType;
  order: AstralWalletOrderStatusDataType;
}

export interface OrderGuestStatusType {
  businessId: number;
  experienceId: number;
  orderId: number;
  dueTime: string;
  maskedEmail: string | null;
  maskedPhone: string | null;
  statuses: OrderGuestStatuses[];
}

export interface HttpCheckoutApiRequest {
  updateCart: UpdateCart;
  checkoutCart: checkoutCartType;
  createAstralWalletTransaction: CreateAstralWalletTransactionType;
  getAstralWalletIframeData: GetAstralWalletIframeDataType;
  astralWalletTransactionStatus: AstralWalletTransactionStatusType;
  refundAstralWalletPayment: RefundAstralWalletPaymentType;
  getOrderConfirmationDetails: GetOrderDetailsConfirmationType;
  retryAstralWalletCheckout: RetryAstralWalletCheckoutType;
  roomValidation: {
    bid: number;
    lastName: string;
    roomNumber: string;
  };
  setGuestOrderStatusNotificationOpt: {
    id: string;
    type: 'Email' | 'Sms';
    isEnabled: boolean;
    data?: string;
  };
}

export interface HttpCheckoutApiResponse {
  iCreateNewCart: {
    status: string;
    cart: {
      id: string;
      currency: string;
      status: 'CREATED' | 'PENDING_CALCULATIONS' | 'REJECTED';
      taxes: Array<any>;
      hash: string;
      summery: {
        subtotal: string;
        tax: string;
        service: string;
        tip: string;
        total: string;
      };
      service_charges: Array<ServiceCharge>;
      coupon: Coupon;
    };
    taxes: Array<Tax>;
  };
  iCheckoutCart: {
    status: string;
    cart: {
      experience_id: number;
      checkout_at: string;
    };
    order: {
      id: number;
      check_number: number;
      payments: PaymentsResponse[];
      guest_order_status_id: number | null;
    };
  };
  iRoomValidation: {
    status: string;
    guests: Array<RoomUser>;
    message: string;
  };
  iCreateAstralWalletTransaction: CreateAstralWalletTransactionResponseType;
  iGetAstralWalletIframeData: GetAstralWalletIframeDataResponseType;
  iAstralWalletTransactionStatus: AstralWalletTransactionStatusResponseType;
  iGetOrderDetailsConfirmation: GetOrderDetailsConfirmationResponseType;
  iOrderGuestStatus: {
    data: OrderGuestStatusType;
  };
}

export interface CardSummary {
  addon_tax: string;
  discount: string;
  inclusive_tax: string;
  service: string;
  subtotal: string | number;
  tax: string;
  tip: string;
  total: string | number;
  itemsQuantity: number;
}

export interface Coupon {
  code: string;
  source: string;
  type: string;
  method: string;
  value: number;
  name: number;
}

export interface Tax {
  name: string;
  amount: string;
  is_inclusive: boolean;
}

export interface RoomUser {
  firstName: string;
  lastName: string;
  token: string;
}

export interface PaymentsResponse {
  id?: number;
  amount: string;
  tip: string;
  type: null | 'EMV' | 'WALLET' | 'ROOM' | 'PAY_IN_PERSON';
  transaction_id: string;
  status: string;
}

type CartDetailsType = {
  name?: string;
  last_name?: string;
  email?: string;
  room_number?: string;
};

type CartItemRequest = {
  pui: string;
  quantity: number;
  comment: string;
  modifiers?: CartItemModifierRequest[];
};

type CartItemModifierRequest = {
  pui: string;
  quantity: number;
  comment: string;
};

export type PaymentTypeOther = {
  type: Omit<
    PaymentMethodsValue,
    PaymentMethodsValue.WALLET | PaymentMethodsValue.ROOM
  >;
};

export type PaymentTypeWallet = {
  type: PaymentMethodsValue.WALLET;
  payment_method_id: number;
  issuer_name: string;
};

type PaymentTypeRoom = {
  type: PaymentMethodsValue.ROOM;
  room_charge_token: string;
};

type CartPaymentType = PaymentTypeOther | PaymentTypeWallet | PaymentTypeRoom;

type CartReceiptType = {
  receive_by: ('EMAIL' | 'PRINTER')[];
};

export type ProductItem = MenuItem & {
  quantity: number;
  comment: string;
  editId?: string;
};

export type CurrentOrder = { [key: string]: ProductItem };

export type TransactionVerification = {
  transaction_id: number;
  status: 'FAILURE';
};
